import { Follow, Member, User } from '@app/models';
import { getFollowDepartmentName } from '@app/models/follow';
import { Milestone } from '@app/models/milestone';
import { Roles } from '@app/models/user';
import {
  AuthActionTypes,
  AuthActionsUnion
} from '@app/modules/core/actions/auth.actions';
import { isComponent } from '@app/product.utils';
import * as rolesUtils from '@app/role.utils';
import { getFollowCountries } from '@core/reducers/follow-country.reducer';
import * as fromFollowShipments from '@core/reducers/follow-shipment.reducer';
import * as fromFollows from '@core/reducers/follow.reducer';
import * as FromFollowArrivals from '@core/reducers/followarrival.reducer';
import * as fromMembers from '@core/reducers/member.reducer';
import * as fromMilestones from '@core/reducers/milestone.reducer';
import * as fromProforma from '@core/reducers/proforma.reducer';
import * as fromRoot from '@core/reducers/root.reducer';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FollowModuleState } from '.';
import {
  FollowActionTypes,
  FollowAreaActionsUnion
} from '../actions/follow.actions';

export interface State {
  selectedId: number;
}

export const INITIAL_STATE: State = {
  selectedId: null
};

export function reducer(
  state: State = INITIAL_STATE,
  action: FollowAreaActionsUnion | AuthActionsUnion
) {
  switch (action.type) {
    case FollowActionTypes.Select:
      return { ...state, selectedId: action.id };

    case AuthActionTypes.Logout:
      return INITIAL_STATE;

    default:
      return state;
  }
}

const getModule = createFeatureSelector('followModule');

const getFollowState = createSelector(
  getModule,
  (state: FollowModuleState) => state.follow
);

export const getId = createSelector(
  getFollowState,
  (state: State) => state.selectedId
);

export const get = createSelector(
  fromFollows.getFollows,
  getId,
  (follows, id) => follows.find(follow => follow.id === id)
);

export const getChildrenIds = createSelector(
  fromFollows.getFollowEntities,
  getId,
  (entities, id) =>
    Object.keys(entities)
      .map(key => entities[key])
      .filter(follow => follow.ParentId === id)
      .map(follow => follow.id)
);

export const getPopulated = createSelector(
  get,
  FromFollowArrivals.getAll,
  fromFollowShipments.getAll,
  fromRoot.getSeasonEntities,
  fromRoot.getDepartmentEntities,
  fromRoot.getFollowGroupEntities,
  (
    follow: Follow,
    followArrivals,
    followShipments,
    seasonEntities,
    departmentEntities,
    followGroupEntities
  ) => {
    const followDepartment =
      (follow && departmentEntities[follow.DepartmentId]) || {};
    const followSeason = (follow && seasonEntities[follow.SeasonId]) || {
      name: 'Temporada no encontrada'
    };

    const Shipments =
      follow && followShipments
        ? followShipments.filter(
          s => s.FollowId === follow.id || s.FollowId === follow.ParentId
        )
        : [];

    const FollowArrival = follow && followArrivals ? 
    followArrivals.filter(f => f['FollowId'] === follow.id || f['FollowId'] === follow.ParentId)
    : []

    const followGroup =
      follow &&
      follow.FollowGroupId &&
      followGroupEntities[follow.FollowGroupId];

    if (follow) {
      let downloadName = 'Desconocido';

      const indexed =
        follow && departmentEntities
          ? getFollowDepartmentName(follow, departmentEntities)
          : 'No encontrado';

          if (follow.ParentId) {
        // If is a children, use the same name as always
        if (follow && followSeason) {
          downloadName = `${follow.plannerId}-${followSeason.name}-${follow.year
            } (${indexed || follow.type})`;
        }
      } else {
        // Use follow group's name for a parent follow
        if (follow &&
          followGroup &&
          followSeason) {
          downloadName = `${followGroup.name}-${followSeason.name}-${follow.year
            } (${indexed || follow.type})`;
        }
      }

      if (follow.type === "VESTUARIO") {
        downloadName = `TEMPLATE - ${follow.plannerId}-${followSeason.name}-${follow.year
        } (${indexed || follow.type})`;
      }

      return {
        ...follow,
        downloadName,
        indexed,
        followGroup,
        Season: followSeason,
        Shipments,
        FollowArrival
      };
    }
  }
);

export const getColors = createSelector(
  getId,
  fromRoot.getColors,
  (id: number, colors) =>
    id ? colors.filter(color => color.FollowId === id) : []
);

export const getBusinesses = createSelector(
  get,
  (follow: Follow) => (follow && follow.Businesses) || []
);

export const getMilestone = createSelector(
  getId,
  fromMilestones.getAll,
  (id: number, milestones: Milestone[]) => {
    const ms = id
      ? milestones.filter(milestone => milestone.FollowId === id)
      : [];
    return ms && ms.length ? ms[0] : null;
  }
);

export const getProducts = createSelector(
  getId,
  getChildrenIds,
  fromRoot.getProductsPopulated,
  (id: number, ids, products) =>
    id
      ? products.filter(p => [...ids, id].includes(p.FollowId) && !p.deletedAt)
      : []
);

export const getDeletedProducts = createSelector(
  getId,
  fromRoot.getProducts,
  (id: number, products) =>
    id ? products.filter(p => p.FollowId === id && p.deletedAt && (!p.code.includes('-DELETE-'))) : []
);

const buildCountriesChartData = (countries, groupedProductCountries) => {
  const labels = Object.keys(countries);
  const length = labels.length;
  const colors = labels.map(
    (c, i) => 'hsl(' + (i * 360) / length + ', 50%, 50%)'
  );
  const barChart = {
    datasets: Object.keys(countries).map((key, i) => {
      return {
        label: key,
        data: [countries[key].totalCountryFob],
        backgroundColor: colors[i]
      };
    })
  };

  const pieChart = {
    datasets: [
      {
        label: 'FOB total por país',
        data: Object.keys(countries).map(key => countries[key].totalCountryFob),
        backgroundColor: colors
      }
    ],
    labels: Object.keys(countries)
  };
  const tableData = {
    headers: [null, ...labels],
    rows: [['FOB Total', ...labels.map(key => countries[key].totalCountryFob)]]
  };

  // SAVE FOr LATER
  // rows: [
  //   ...Object.keys(groupedProductCountries).map(key => {
  //     return [
  //       groupedProductCountries[key][labels[0]].productCode,
  //       ...labels.map(label => groupedProductCountries[key][label].totalFob)
  //     ];
  //   }),
  //   ['FOB Total', ...labels.map(key => countries[key].totalCountryFob)]
  // ]

  return { barChart, pieChart, tableData };
};

const buildUnitsPerCountryChartData = (countries, groupedProductCountries) => {
  const labels = Object.keys(countries);
  const tableData = {
    headers: [null, ...labels],
    rows: [
      ...Object.keys(groupedProductCountries).map(key => {
        return [
          groupedProductCountries[key][labels[0]].productCode,
          ...labels.map(label => groupedProductCountries[key][label].totalUnits)
        ];
      })
    ]
  };

  return { tableData };
};

const buildFobPerCountryChartData = (countries, groupedProductCountries) => {
  const labels = Object.keys(countries);
  const tableData = {
    headers: [null, ...labels],
    rows: [
      ...Object.keys(groupedProductCountries).map(key => {
        return [
          groupedProductCountries[key][labels[0]].productCode,
          ...labels.map(label => groupedProductCountries[key][label].totalFob)
        ];
      })
    ]
  };

  return { tableData };
};

const buildCountryChartData = (countries, groupedProductCountries) => {
  const countryName = Object.keys(countries)[0];
  const tableData = {
    headers: ['Código Regional', 'Unidades', 'FOB Total'],
    rows: [
      ...Object.keys(groupedProductCountries).map(key => {
        return [
          groupedProductCountries[key][countryName].productCode,
          groupedProductCountries[key][countryName].totalUnits,
          groupedProductCountries[key][countryName].totalFob
        ];
      })
    ]
  };

  return { tableData };
};

const buildProductsPerCountryChartData = (
  countries,
  groupedProductCountries
) => {
  const labels = Object.keys(countries);
  const tableData = {
    headers: [null, ...labels],
    rowsSet: [
      [
        ...Object.keys(groupedProductCountries).map(key => {
          return [
            groupedProductCountries[key][labels[0]].productCode,
            ...labels.map(
              label => groupedProductCountries[key][label].totalUnits
            )
          ];
        })
      ],
      [
        ...Object.keys(groupedProductCountries).map(key => {
          return [
            groupedProductCountries[key][labels[0]].productCode,
            ...labels.map(label => groupedProductCountries[key][label].totalFob)
          ];
        })
      ]
    ]
  };

  return { tableData };
};

const buildProviderShipmentsChartData = (groupedData, providerEntities) => {
  const labels = ['E1', 'E2', 'E3', 'E4', 'E5', 'E6'];
  const consolidatedShipments = groupedData
    .reduce((arr, item) => {
      return [...arr, ...Object.keys(item.shipments)];
    }, [])
    .filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

  const length = groupedData.length;
  const colors = groupedData.map(
    (c, i) => 'hsl(' + (i * 360) / length + ', 50%, 50%)'
  );

  const pieChart = {
    datasets: [
      {
        label: 'E1',
        data: groupedData.map(i => i.shipments['e1'] || 0),
        backgroundColor: colors
      },
      {
        label: 'E2',
        data: groupedData.map(i => i.shipments['e2'] || 0),
        backgroundColor: colors
      },
      {
        label: 'E3',
        data: groupedData.map(i => i.shipments['e3'] || 0),
        backgroundColor: colors
      },
      {
        label: 'E4',

        data: groupedData.map(i => i.shipments['e4'] || 0),
        backgroundColor: colors
      },
      {
        label: 'E5',
        data: groupedData.map(i => i.shipments['e5'] || 0),
        backgroundColor: colors
      },
      {
        label: 'E6',
        data: groupedData.map(i => i.shipments['e6'] || 0),
        backgroundColor: colors
      }
    ],
    labels: groupedData.map(
      i => providerEntities[i.ProviderId].name || i.ProviderId
    )
  };

  const lineChart = {
    labels,
    datasets: groupedData.map((provider, i) => {
      return {
        label: provider.Provider.name,
        data: consolidatedShipments.map(key => provider.shipments[key] || 0),
        borderColor: colors[i],
        // steppedLine: true,
        fill: false
      };
    })
  };
  const barChart = {
    labels,
    datasets: groupedData.map((provider, i) => {
      return {
        label: provider.Provider.name,
        data: consolidatedShipments.map(key => provider.shipments[key] || 0),
        backgroundColor: colors[i]
        // steppedLine: true,
      };
    })
  };

  const tableData = {
    headers: [null, ...labels],
    rows: groupedData.map((provider, i) => {
      return [
        provider.Provider.name,
        ...consolidatedShipments.map(key => provider.shipments[key] || 0)
      ];
    })
  };

  return { lineChart, barChart, pieChart, tableData };
};

export const getOverviewData = createSelector(
  getPopulated,
  fromRoot.getProviderEntities,
  fromRoot.getCountryEntities,
  getProducts,

  (follow, providerEntities, countryEntities, products) => {
    if (products && providerEntities) {
      const groupedByProvider = products.reduce((obj, product) => {
        if (product.ProviderId) {
          if (obj[product.ProviderId]) {
            obj[product.ProviderId].push(getSummedCountryShipments(product));
          } else {
            obj[product.ProviderId] = [getSummedCountryShipments(product)];
          }
        }
        return obj;
      }, {});

      const groupedData = Object.keys(groupedByProvider).map(providerId => {
        return {
          ProviderId: parseInt(providerId, 10),
          Provider: providerEntities[parseInt(providerId, 10)],
          shipments: getSummedKeys(groupedByProvider[providerId])
        };
      });

      const countries = {};
      const groupedProductCountries = {};
      products.forEach(product => {
        const productCountriesData = {};
        product.ProductCountries.forEach(pc => {
          const units = pc.shipments
            ? Object.keys(pc.shipments).reduce((sum, key) => {
              return sum + pc.shipments[key];
            }, 0)
            : 0;

          const fobType = pc && pc.metadata && pc.metadata.fobType;

          const fob =
            product && product.metadata && fobType
              ? (fobType === 'regionalFOB'
                ? product.metadata['finalFOB'] ||
                product.metadata['initialFOB']
                : product.metadata[fobType]) || 0
              : 0;
          const countryName =
            countryEntities[pc.CountryId] && countryEntities[pc.CountryId].name;
          if (countryName) {
            if (countries[countryName]) {
              countries[countryName].productsData = [
                ...countries[countryName].productsData,
                {
                  ProductId: product.id,
                  productCode: product.code,
                  totalUnits: units,
                  totalFob: fob * units
                }
              ];
              countries[countryName].totalCountryFob += fob * units;
            } else {
              countries[countryName] = {
                productsData: [
                  {
                    ProductId: product.id,
                    totalUnits: units,
                    totalFob: fob * units
                  }
                ],
                totalCountryFob: fob * units
              };
            }
            if (groupedProductCountries[product.id]) {
              groupedProductCountries[product.id][countryName] = {
                productCode: product.code,
                totalUnits: units,
                totalFob: fob * units
              };
            } else {
              groupedProductCountries[product.id] = {
                [countryName]: {
                  productCode: product.code,
                  totalUnits: units,
                  totalFob: fob * units
                }
              };
            }
          }
        });
      });
      const countriesChartData = buildCountriesChartData(
        countries,
        groupedProductCountries
      );
      const providerShipmentsChartData = buildProviderShipmentsChartData(
        groupedData,
        providerEntities
      );

      const unitsPerCountryChartData = buildUnitsPerCountryChartData(
        countries,
        groupedProductCountries
      );

      const fobPerCountryChartData = buildFobPerCountryChartData(
        countries,
        groupedProductCountries
      );

      const productsPerCountryChartData = buildProductsPerCountryChartData(
        countries,
        groupedProductCountries
      );

      return {
        providerShipmentsChartData,
        countriesChartData,
        unitsPerCountryChartData,
        fobPerCountryChartData,
        productsPerCountryChartData
      };
    }
  }
);

export const getCountryOverviewData = createSelector(
  getPopulated,
  fromRoot.getProviderEntities,
  fromRoot.getCountryEntities,
  fromRoot.getSelectedCountryId,
  getProducts,

  (follow, providerEntities, countryEntities, countryId, products) => {
    if (products && providerEntities) {
      const groupedByProvider = products.reduce((obj, product) => {
        product.ProductCountries = countryId
          ? product.ProductCountries.filter(pc => pc.CountryId === countryId)
          : product.ProductCountries;
        if (product.ProviderId) {
          if (obj[product.ProviderId]) {
            obj[product.ProviderId].push(getSummedCountryShipments(product));
          } else {
            obj[product.ProviderId] = [getSummedCountryShipments(product)];
          }
        }
        return obj;
      }, {});

      const groupedData = Object.keys(groupedByProvider).map(providerId => {
        return {
          ProviderId: parseInt(providerId, 10),
          Provider: providerEntities[parseInt(providerId, 10)],
          shipments: getSummedKeys(groupedByProvider[providerId])
        };
      });

      const countries = {};
      const groupedProductCountries = {};
      products.forEach(product => {
        product.ProductCountries = countryId
          ? product.ProductCountries.filter(pc => pc.CountryId === countryId)
          : product.ProductCountries;
        const productCountriesData = {};
        product.ProductCountries.forEach(pc => {
          const units = pc.shipments
            ? Object.keys(pc.shipments).reduce((sum, key) => {
              return sum + pc.shipments[key];
            }, 0)
            : 0;

          const fobType = pc && pc.metadata && pc.metadata.fobType;

          const fob =
            product && product.metadata && fobType
              ? (fobType === 'regionalFOB'
                ? product.metadata['finalFOB'] ||
                product.metadata['initialFOB']
                : product.metadata[fobType]) || 0
              : 0;
          const countryName =
            countryEntities[pc.CountryId] && countryEntities[pc.CountryId].name;
          if (countryName) {
            if (countries[countryName]) {
              countries[countryName].productsData = [
                ...countries[countryName].productsData,
                {
                  ProductId: product.id,
                  productCode: product.code,
                  totalUnits: units,
                  totalFob: fob * units
                }
              ];
              countries[countryName].totalCountryFob += fob * units;
            } else {
              countries[countryName] = {
                productsData: [
                  {
                    ProductId: product.id,
                    totalUnits: units,
                    totalFob: fob * units
                  }
                ],
                totalCountryFob: fob * units
              };
            }
            if (groupedProductCountries[product.id]) {
              groupedProductCountries[product.id][countryName] = {
                productCode: product.code,
                totalUnits: units,
                totalFob: fob * units
              };
            } else {
              groupedProductCountries[product.id] = {
                [countryName]: {
                  productCode: product.code,
                  totalUnits: units,
                  totalFob: fob * units
                }
              };
            }
          }
        });
      });

      const countryChartData = buildCountryChartData(
        countries,
        groupedProductCountries
      );

      return {
        countryChartData
      };
    }
  }
);

function getSummedCountryShipments(product) {
  return product.ProductCountries.reduce((obj, country) => {
    Object.keys(country.shipments).forEach(key => {
      if (obj[key]) {
        obj[key] = obj[key] + country.shipments[key];
      } else {
        obj[key] = country.shipments[key];
      }
    });
    return obj;
  }, {});
}

function getSummedKeys(array) {
  return array.reduce((obj, arrayItem) => {
    Object.keys(arrayItem).forEach(key => {
      if (obj[key]) {
        obj[key] = obj[key] + arrayItem[key];
      } else {
        obj[key] = arrayItem[key];
      }
    });
    return obj;
  }, {});
}

// export const getProforma = createSelector(
//   getProducts,
//   getPopulated,
//   fromProforma.getAll,
//   (products, follow, proformas) => {
//     const all = {};
//     products
//       .filter(p => !productHelpers.isComponent(p))
//       .forEach(product => {
//         const portName = product.Port && product.Port.name;
//         const providerName = product.Provider && product.Provider.name;
//         const productCountries =
//           product.ProductCountries && product.ProductCountries.length;

//         if (providerName && portName && productCountries) {
//           const filteredShipments =
//             product.Provider && follow && follow.Shipments
//               ? follow.Shipments.filter(
//                   s => s.CountryId === product.Provider.CountryId
//                 )
//               : [];

//           if (filteredShipments.length) {
//             all[providerName] = all[providerName] || {};
//             if (!all[providerName][portName]) {
//               all[providerName][portName] = filteredShipments.reduce(
//                 (shipmentsObj, shipment, i) => {
//                   const id = shipment.id;
//                   if (!shipmentsObj[i]) {
//                     shipmentsObj[i] = {
//                       shipmentId: shipment.id,
//                       PortId: product.PortId,
//                       ProviderId: product.ProviderId,
//                       shipmentDate: shipment.departureDate,
//                       countries: product.ProductCountries.filter(
//                         pc => pc.businessUnit
//                       ),
//                       products: [product]
//                     };
//                   }

//                   return shipmentsObj;
//                 },
//                 {}
//               );
//             } else {
//               Object.keys(all[providerName][portName]).forEach(key => {
//                 all[providerName][portName][key] = {
//                   ...all[providerName][portName][key],
//                   products: [
//                     ...all[providerName][portName][key]['products'],
//                     product
//                   ]
//                 };
//               });
//             }
//           }
//         }
//       });
//     return all;
//   }
// );

export const getProforma = createSelector(
  getProducts,
  getPopulated,
  fromProforma.getAll,
  (products, follow, proformas) => {
    const all = {};
    products
      .filter(p => !isComponent(p))
      .forEach(product => {
        const portName = product.Port && product.Port.name;
        const providerName = product.Provider && product.Provider.name;
        const productCountries =
          product.ProductCountries && product.ProductCountries.length;

        if (providerName && portName && productCountries) {
          const filteredShipments =
            product.Provider && follow && follow.Shipments
              ? follow.Shipments.filter(
                s => s.CountryId === product.Provider.CountryId
              )
              : [];

          if (filteredShipments.length) {
            all[product.ProviderId] = all[product.ProviderId] || {};
            if (!all[product.ProviderId][product.PortId]) {
              all[product.ProviderId][
                product.PortId
              ] = filteredShipments.reduce((shipmentsObj, shipment, i) => {
                const id = shipment.id;
                if (!shipmentsObj[i]) {
                  shipmentsObj[i] = {
                    shipmentId: shipment.id,
                    PortId: product.PortId,
                    ProviderId: product.ProviderId,
                    shipmentDate: shipment.departureDate,
                    countries: product.ProductCountries.filter(
                      pc => pc.businessUnit
                    ),
                    products: [product]
                  };
                }

                return shipmentsObj;
              }, {});
            } else {
              Object.keys(all[product.ProviderId][product.PortId]).forEach(
                key => {
                  all[product.ProviderId][product.PortId][key] = {
                    ...all[product.ProviderId][product.PortId][key],
                    products: [
                      ...all[product.ProviderId][product.PortId][key][
                      'products'
                      ],
                      product
                    ]
                  };
                }
              );
            }
          }
        }
      });
    return all;
  }
);

export const getSelectedFollowCountries = createSelector(
  getId,
  getChildrenIds,
  getFollowCountries,
  (id: number, ids: number[], countries) =>
    id && ids ? countries.filter(c => [...ids, id].includes(c.FollowId)) : []
);

export const getMembers = createSelector(
  getId,
  fromMembers.getMembers,
  (id: number, members) => (id ? members.filter(m => m.FollowId === id) : [])
);

export const getMembersAllFollows = createSelector(
  getId,
  fromMembers.getMembers,
  (id: number, members) => members
)

export const getRegionalBuyerMembers = createSelector(
  getId,
  fromMembers.getMembers,
  (id: number, members) =>
    id
      ? members.filter(m => m.FollowId === id && m.role === 'regional-buyer')
      : []
);


// export const getRegionalManagerMembers = createSelector(
//   getId,
//   fromMembers.getMembers,
//   (id: number, members) =>
//     id
//       ? members.filter(m => m.FollowId === id && m.role === 'regional-manager')
//       : []
// );

export const getCountryBuyers = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        // .filter(m => m.role === Roles.CountryBuyer)
        .filter(m => rolesUtils.isCountryUser(m.role))
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getCountryUsers = createSelector(
  getId,
  getMembers,
  (id: number, members) => {
    if (id && members && members.length) {
      // TODO: Use util function to check roles
      return members
        .filter(
          x => x.role === Roles.CountryBuyer
            || x.role === Roles.CountryPlanner
            || x.role === Roles.CountryManager
            || x.role === Roles.CountryBoss
        )
        .map(x => ({
          ...x,
          fullName: `${x.firstName} ${x.lastName}`
        }));
    }

    return [];
  }
);

export const getDesigners = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.Designer)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getRegionalBuyers = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.RegionalBuyer)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getCategoryBuyerAndProductManager = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.CategoryManger)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getRegionalBuyerAndProductManager = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.RegionalBuyer || m.role === Roles.ProductManager)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getRegionalManagers = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.RegionalManager)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getMerchants = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.Merchant)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getRegionalBosses = createSelector(
  getId,
  getMembers,
  (id: number, members) =>
    id
      ? members
        .filter(m => m.role === Roles.RegionalBoss)
        .map(member => ({
          ...member,
          fullName: `${member.firstName} ${member.lastName}`
        }))
      : []
);

export const getAvailableUsers = createSelector(
  fromRoot.getUsers,
  getMembers,
  (users: User[], members: Member[]) => {
    const memberIds = members ? members.map(m => m.id) : [];
    return users && members
      ? users.filter(user => !memberIds.includes(user.id))
      : [];
  }
);

export const getCountryDrillDown = createSelector(
  getProducts,
  fromRoot.getMe,
  (products, me) => {
    if (products) {
      const drillDown = products.reduce((obj, product) => {
        const productCountry =
          me &&
          product &&
          product.ProductCountries &&
          product.ProductCountries.find(
            pc =>
              pc.CountryId === me.CountryId &&
              pc.businessUnit === me.businessUnit
          );
        const articleGroup =
          (productCountry && productCountry.CountryDepartmentId) ||
          'Sin grupo de artículos';
        if (obj[articleGroup]) {
          obj[articleGroup] = [...obj[articleGroup], product];
        } else {
          obj[articleGroup] = [product];
        }
        return obj;
      }, {});
      return drillDown;
    }
  }
);

export const getArrivalChartsData = createSelector(
  get,
  getProducts,
  fromRoot.getFollowShipmentsPopulated,
  (follow, products, shipments) => {
    const isReady =
      follow &&
      (products && products.length > 0) &&
      (shipments && shipments.length > 0);

    let boxes = { labels: [], datasets: [] };
    let fob = { labels: [], datasets: [] };
    let cbms = { labels: [], datasets: [] };

    if (isReady) {
      // Group products by ShipmentId
      const productsByShipmentId = products.reduce((acc, product) => {
        product.ProductCountries.forEach(pc => {
          if (pc.Shipments) {
            pc.Shipments.forEach(productShipment => {
              if (!acc[productShipment.ShipmentId]) {
                acc[productShipment.ShipmentId] = [];
              }

              acc[productShipment.ShipmentId].push({
                ...product,
                productShipment
              });
            });
          }
        });

        return acc;
      }, {});

      // Create X axis labels with shipment dates
      const labels = shipments.map(x => x.departureDate);

      boxes.labels = labels;
      fob.labels = labels;
      cbms.labels = labels;

      let totals = { boxes: [], cbms: [], fob: [] };

      shipments.forEach(shipment => {
        const shipmentProducts = productsByShipmentId[shipment.id];

        if (shipmentProducts) {
          const shipmentTotals = shipmentProducts.reduce(
            (acc, item) => {
              if (item.metadata) {
                if (item.metadata.master) {
                  acc.boxes +=
                    item.productShipment.units / item.metadata.master;

                  if (item.metadata.closedFOB) {
                    acc.fob +=
                      (item.productShipment.units *
                        item.metadata.master *
                        item.metadata.closedFOB) /
                      1000;
                  }
                }

                if (item.metadata.cbmPerCarton) {
                  acc.cbms +=
                    item.productShipment.units * item.metadata.cbmPerCarton;
                }
              }

              return acc;
            },
            { boxes: 0, cbms: 0, fob: 0 }
          );

          totals.boxes.push(shipmentTotals.boxes.toFixed(2));
          totals.cbms.push(shipmentTotals.cbms.toFixed(2));
          totals.fob.push(shipmentTotals.fob.toFixed(2));
        }
      });

      boxes.datasets.push({ label: 'Cajas', data: totals.boxes });
      fob.datasets.push({ label: 'FOB', data: totals.fob });
      cbms.datasets.push({ labels: 'CBM', data: totals.cbms });
    }

    return {
      isReady,
      boxes,
      cbms,
      fob
    };
  }
);

export const getMilestonesPopulated = createSelector(
  getId,
  getMembers,
  fromMilestones.getAll,
  (followId: number, members, milestones) => {
    milestones = milestones.filter(m => m.FollowId === followId);
    milestones.forEach(m => {
      m.Author = members.find(u => u.id === m.AuthorId);
    });

    return milestones;
  }
);
